import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tag, Button, Modal, Select, Input, DatePicker, message, Empty, Tooltip } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { getBadgeTypes, getStudentBadges, assignStudentBadge, removeStudentBadge } from '../../api/studentBadges';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const KIND_LABELS = { persistent: 'Kalıcı Özellik', one_time: 'Geçici Durum' };

// Gecici rozetin bugune gore durumu - ogrenci profili artik tum rozetleri
// (gecmis/gelecek dahil) gosterdigi icin personel hangisinin su an
// gecerli oldugunu ayirt edebilsin.
const getOneTimeStatus = (badge) => {
  if (!badge.starts_at || !badge.ends_at) return null;
  const today = new Date().toISOString().slice(0, 10);
  if (today < badge.starts_at) return { label: 'Yaklaşıyor', color: 'default' };
  if (today > badge.ends_at) return { label: 'Bitti', color: 'default' };
  return { label: 'Aktif', color: 'success' };
};

// Rozet VERME/KALDIRMA varsayılan olarak yöneticide; ama kişi bazında
// bir öğretmene de verilebiliyor (backend: `student_badges.award`).
//
// ⚠ BURASI BİR DÖNEM YALNIZCA ROLE BAKIYORDU ve yetkiyi vermek işe
// yaramıyordu: yönetici panelden "Öğrenciye Rozet Ver" diyordu, backend
// açılıyordu, ama öğretmen ekranda hiçbir düğme görmüyordu — giriş yolu
// yoktu (26.10.2026, Eren Bey bildirdi; taklit modunda bakıldı).
//
// CLAUDE.md'nin "rol kapısına İZNİ de ekle, İKİ yeri birden" kuralının
// bileşen hali. Aynı kaçış kapısı deseni: ParentProfileChangePendingPanel.
const MANAGEMENT_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const StudentBadgesPanel = ({ studentId }) => {
  const { user } = useAuthStore();
  const canManage = MANAGEMENT_ROLES.includes(user?.role)
    || (user?.permissions || []).includes('student_badges.award');

  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedType = types.find(t => t.id === selectedTypeId);
  const isOneTime = selectedType?.kind === 'one_time';

  const fetchBadges = useCallback(() => {
    setLoading(true);
    getStudentBadges(studentId)
      .then(res => setBadges(res.data || []))
      .catch(() => message.error('Rozetler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const openModal = () => {
    setSelectedTypeId(null);
    setDateRange(null);
    setNote('');
    getBadgeTypes()
      .then(res => setTypes(res.data || []))
      .catch(() => message.error('Rozet türleri yüklenemedi.'));
    setModalOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedTypeId) { message.warning('Lütfen bir rozet seçin.'); return; }
    if (isOneTime && (!dateRange || dateRange.length !== 2)) {
      message.warning('Geçici rozetler için başlangıç ve bitiş tarihi seçiniz.');
      return;
    }
    setSaving(true);
    try {
      await assignStudentBadge(studentId, {
        student_badge_type_id: selectedTypeId,
        note: note.trim() || undefined,
        ...(isOneTime ? {
          starts_at: dateRange[0].format('YYYY-MM-DD'),
          ends_at:   dateRange[1].format('YYYY-MM-DD'),
        } : {}),
      });
      message.success('Rozet eklendi.');
      setModalOpen(false);
      fetchBadges();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Rozet eklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (badgeId) => {
    try {
      await removeStudentBadge(studentId, badgeId);
      message.success('Rozet kaldırıldı.');
      fetchBadges();
    } catch {
      message.error('Rozet kaldırılamadı.');
    }
  };

  // Kalici rozetler zaten atanmissa secim listesinde tekrar gorunmesin
  // (backend'de de tek seferlik). Gecici rozetler ise farkli tarih
  // araliklariyla birden fazla kez eklenebildigi icin listeden hic
  // cikarilmiyor - cakisma kontrolu backend'de yapiliyor.
  const assignedPersistentTypeIds = badges
    .filter(b => b.type?.kind === 'persistent')
    .map(b => b.student_badge_type_id);
  const availableTypes = types.filter(t => !assignedPersistentTypeIds.includes(t.id));
  const grouped = {
    persistent: availableTypes.filter(t => t.kind === 'persistent'),
    one_time:   availableTypes.filter(t => t.kind === 'one_time'),
  };

  return (
    <Card
      title="Rozetler"
      size="small"
      className="mb-4"
      loading={loading}
      extra={canManage && (
        <Button size="small" icon={<PlusOutlined />} onClick={openModal}>Rozet Ekle</Button>
      )}
    >
      {badges.length === 0 ? (
        <Empty description="Rozet yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="flex flex-wrap gap-2">
          {badges.map(b => {
            const status = getOneTimeStatus(b);
            return (
              <Tooltip
                key={b.id}
                title={[
                  b.starts_at && b.ends_at
                    ? `${new Date(b.starts_at).toLocaleDateString('tr-TR')} — ${new Date(b.ends_at).toLocaleDateString('tr-TR')}`
                    : null,
                  status?.label,
                  b.note,
                  b.assigned_by ? `Ekleyen: ${b.assigned_by.name} ${b.assigned_by.surname}` : null,
                ].filter(Boolean).join(' — ') || undefined}
              >
                <Tag
                  color={b.type?.color}
                  closable={canManage}
                  onClose={(e) => { e.preventDefault(); handleRemove(b.id); }}
                  closeIcon={canManage ? <CloseOutlined style={{ fontSize: 10 }} /> : undefined}
                  style={{ fontSize: 13, padding: '2px 8px', opacity: status && status.label !== 'Aktif' ? 0.6 : 1 }}
                >
                  {b.type?.name}
                  {status && status.label !== 'Aktif' ? ` (${status.label})` : ''}
                </Tag>
              </Tooltip>
            );
          })}
        </div>
      )}

      {canManage && (
        <Modal
          title="Rozet Ekle"
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={handleAssign}
          okText="Ekle"
          cancelText="Vazgeç"
          confirmLoading={saving}
        >
          <Select
            style={{ width: '100%' }}
            placeholder="Rozet seçiniz"
            value={selectedTypeId}
            onChange={(v) => { setSelectedTypeId(v); setDateRange(null); }}
            notFoundContent="Uygun rozet türü yok"
          >
            {grouped.persistent.length > 0 && (
              <Select.OptGroup label={KIND_LABELS.persistent}>
                {grouped.persistent.map(t => (
                  <Select.Option key={t.id} value={t.id}>
                    <Tag color={t.color} style={{ marginRight: 4 }}>{t.name}</Tag>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
            {grouped.one_time.length > 0 && (
              <Select.OptGroup label={KIND_LABELS.one_time}>
                {grouped.one_time.map(t => (
                  <Select.Option key={t.id} value={t.id}>
                    <Tag color={t.color} style={{ marginRight: 4 }}>{t.name}</Tag>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </Select>

          {isOneTime && (
            <RangePicker
              className="mt-3"
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              value={dateRange}
              onChange={setDateRange}
              placeholder={['Başlangıç', 'Bitiş']}
            />
          )}

          <TextArea
            className="mt-3"
            placeholder="Not (opsiyonel)..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={500}
          />
        </Modal>
      )}
    </Card>
  );
};

export default StudentBadgesPanel;
